import React, {useState} from 'react';
import {THEME} from 'app/constants';
import {Icons} from 'app/util/icons';
import {StopTranscriptionButton} from 'app/components/sharedReactComponents/StartStopTranscriptionButton/StopTranscriptionButton';
import {TranscribeDuration} from 'app/components/sharedReactComponents/TranscribeDuration';
import {PauseTranscriptionButton} from 'app/components/sharedReactComponents/StartStopTranscriptionButton/PauseTranscriptionButton';
import {StartTranscriptionButton} from 'app/components/sharedReactComponents/StartStopTranscriptionButton/StartTranscriptionButton';
import {LiveScryptDeviceModel} from 'app/components/DeviceDetails/Models/LiveScryptDeviceModel';
import {DeviceAudioMeter} from 'app/components/sharedReactComponents/DeviceAudioMeter/DeviceAudioMeter';
import {isDisabledAudioMeter} from 'app/components/sharedReactComponents/DeviceAudioMeter/utils';
import {TranscriptionPopover} from 'app/components/features/transcribing';
import {Component} from 'app/types/common';

interface Props {
  device: LiveScryptDeviceModel;
}

export function TranscriberControls({device}: Props) {
  const {active, paused, status, sessionId} = device.getTranscribingInfo();
  const disabled = device.isOffline();

  const renderPause = () => (
    <>
      <StopTranscriptionButton
        className="transcriber-controls__stop-button"
        data-id="stop_transcription_button"
        disabled={disabled}
        action={async () => device.stopTranscribing()}
      >
        {Icons.stop().reactComponent()}
      </StopTranscriptionButton>

      <TranscribeDuration
        className="transcriber-controls__duration"
        deviceId={device.getId()}
        // TODO!: redo
        sessionId={sessionId!}
        status={status}
      />

      {paused ? (
        <StartTranscription
          data-id="start_transcription_button"
          className="transcriber-controls__resume-button"
          device={device}
          mode="resume"
        />
      ) : (
        <PauseTranscriptionButton
          className="transcriber-controls__pause-button"
          data-id="pause_transcription_button"
          disabled={disabled}
          action={async () => device.pauseTranscribing()}
        />
      )}
    </>
  );

  return (
    <div className="transcriber-controls">
      <div className="transcriber-controls__buttons">
        {active ? (
          renderPause()
        ) : (
          <StartTranscription data-id="start_transcription_button" device={device} mode="start" />
        )}
      </div>

      <DeviceAudioMeter id={device.getId()} disabled={isDisabledAudioMeter(device)} />
    </div>
  );
}

interface StartTranscriptionProps extends Component {
  device: LiveScryptDeviceModel;
  mode: 'start' | 'resume';
  paymentAccess?: boolean;
}

function StartTranscription({
  dataId,
  className,
  device,
  mode,
  paymentAccess,
}: StartTranscriptionProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const isStart = mode === 'start';

  const cap = device.caps.transcribing;

  const handleClick = async (e: React.MouseEvent<HTMLElement>) => {
    if (!cap) {
      return null;
    }

    if (!cap.enabled) {
      setAnchorEl(e.currentTarget);

      return;
    }

    if (isStart) {
      await device.startTranscribing();
    } else {
      await device.resumeTranscribing();
    }
  };

  if (!cap) {
    return null;
  }

  return (
    <>
      <StartTranscriptionButton
        className={className}
        data-id={dataId}
        disabled={device.isOffline()}
        theme={isStart ? THEME.PRIMARY : undefined}
        action={handleClick}
      >
        {isStart ? undefined : Icons.play().reactComponent()}
      </StartTranscriptionButton>

      <TranscriptionPopover
        capability={cap}
        anchorEl={anchorEl}
        paymentAccess={paymentAccess ?? false}
        setAnchorEl={setAnchorEl}
      />
    </>
  );
}
